// The "you're on the list" room for a verified-but-not-allowlisted user (access === "pending").
// It grants NO app access. It reads its OWN waitlist doc live (onSnapshot) so the "saving…" →
// "on the list ✓" flip fires when the fire-and-forget create commits, fires an idempotent
// joinWaitlist itself (self-heals a failed AuthContext create + backs the retry), and
// auto-advances into /create when the admin approves (open-tab live moment; the approval email
// covers a closed tab). A declined tombstone is shown as the SAME soft grace — a deliberate
// silent soft-deny that preserves the speakeasy mystery-bounce.
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/auth";
import { isAllowlisted } from "@/lib/allowlist";
import { joinWaitlist, watchWaitlist, type WaitlistEntry } from "@/lib/waitlist";
import styles from "./PendingScreen.module.css";

export default function PendingScreen(): JSX.Element | null {
  const { user } = useAuth();
  const [entry, setEntry] = useState<WaitlistEntry | null>(null);
  const [failed, setFailed] = useState(false);

  const email = user?.email ?? null;

  const attemptJoin = useCallback(() => {
    if (!user) return;
    setFailed(false);
    void joinWaitlist(user).catch(() => setFailed(true));
  }, [user]);

  // Idempotent join (getDoc-first) — self-heals a failed AuthContext create + backs Retry.
  useEffect(() => { attemptJoin(); }, [attemptJoin]);

  // Live own-doc read — the "saving…" → "on the list ✓" flip fires when the create commits.
  useEffect(() => {
    if (!email) return;
    return watchWaitlist(email, (e) => setEntry(e), () => setFailed(true));
  }, [email]);

  // Auto-advance: when ken approves, an open tab notices (window focus + a slow poll) and
  // reloads → RequireAuth passes → /create. Skipped under the forced-pending emulator hatch
  // (isAllowlisted short-circuits true there → it would reload straight back into pending).
  useEffect(() => {
    if (!user || import.meta.env.VITE_FORCE_PENDING === "1") return;
    let cancelled = false;
    const probe = async () => {
      if (await isAllowlisted(user.email) && !cancelled) window.location.reload();
    };
    const id = window.setInterval(() => void probe(), 20_000);
    const onFocus = () => void probe();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user]);

  if (!user) return null;

  // pending / declined (silent soft-deny) / approved all read as "on the list"; approved also
  // triggers the auto-advance reload above.
  const onList = entry != null;

  return (
    <div className={styles.room}>
      <p className={styles.lettering}>This is not for everyone.</p>
      {failed ? (
        <p className={styles.status}>
          Couldn&rsquo;t save your spot.{" "}
          <button className={styles.retry} onClick={attemptJoin}>Retry</button>
        </p>
      ) : onList ? (
        <p className={styles.status}>You&rsquo;re on the list &#10003; — a human reviews requests.</p>
      ) : (
        <p className={styles.status}>Saving your spot&hellip;</p>
      )}
      <p className={styles.privacy}>We store your name + email so a human can review your request.</p>
      <button className={styles.signout} onClick={() => void logout()}>Sign out</button>
    </div>
  );
}
